import { cn } from "../lib/utils";

const Button = ({ className, variant = "default", size = "default", children, ...props }) => {
  const variants = {
    default: "bg-yellow-400 text-black hover:bg-yellow-500 font-semibold border-0",
    secondary: "bg-gray-700 text-gray-100 hover:bg-gray-600 border border-gray-500",
    outline: "border-2 border-yellow-500 bg-transparent text-yellow-400 hover:bg-yellow-500 hover:text-black hover:border-yellow-400",
    destructive: "bg-red-600 text-white hover:bg-red-700 border-0",
    ghost: "text-gray-400 hover:bg-gray-700 hover:text-white bg-transparent",
    link: "text-yellow-400 underline-offset-4 hover:underline hover:text-yellow-300"
  };

  const sizes = {
    default: "h-10 px-4 py-2",
    sm: "h-9 rounded-full px-3 text-sm",
    lg: "h-11 rounded-full px-8",
    icon: "h-10 w-10"
  };

  return (
    <button
      className={cn(
        "io-action-button inline-flex items-center justify-center rounded-full text-sm font-medium ring-offset-gray-900 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 shadow-sm",
        variants[variant],
        sizes[size],
        className
      )}
      {...props}
      data-ui-variant={variant}
    >
      {children}
    </button>
  );
};

const Input = ({ className, type = "text", ...props }) => {
  return (
    <input
      type={type}
      className={cn(
        "flex h-10 w-full rounded-md border-2 border-gray-600 bg-white px-3 py-2 text-sm text-black ring-offset-gray-900 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400 focus-visible:ring-offset-2 focus-visible:border-yellow-400 disabled:cursor-not-allowed disabled:opacity-50 transition-colors",
        className
      )}
      {...props}
    />
  );
};

const Label = ({ className, ...props }) => {
  return (
    <label
      className={cn(
        "text-sm font-semibold text-yellow-400 leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
        className
      )}
      {...props}
    />
  );
};

const Card = ({ className, ...props }) => {
  return (
    <div
      className={cn(
        "rounded-lg border border-gray-700 bg-gray-800 text-gray-800 dark:text-gray-200 shadow-sm transition-colors duration-200",
        className
      )}
      {...props}
    />
  );
};

const CardHeader = ({ className, ...props }) => {
  return (
    <div className={cn("flex flex-col space-y-1.5 p-6", className)} {...props} />
  );
};

const CardTitle = ({ className, ...props }) => {
  return (
    <h3
      className={cn(
        "text-2xl font-semibold leading-none tracking-tight text-yellow-400",
        className
      )}
      {...props}
    />
  );
};

const CardContent = ({ className, ...props }) => {
  return <div className={cn("p-6 pt-0", className)} {...props} />;
};

const Alert = ({ className, variant = "default", ...props }) => {
  const variants = {
    default: "bg-gray-800 text-gray-100 border-gray-700",
    destructive: "bg-red-950 text-red-400 border-red-800",
    success: "bg-green-950 text-green-400 border-green-800"
  };

  return (
    <div
      className={cn(
        "relative w-full rounded-lg border p-4",
        variants[variant],
        className
      )}
      {...props}
    />
  );
};

const AlertDescription = ({ className, ...props }) => {
  return (
    <div
      className={cn("text-sm [&_p]:leading-relaxed", className)}
      {...props}
    />
  );
};

export { Button, Input, Label, Card, CardHeader, CardTitle, CardContent, Alert, AlertDescription };